/**
 * Typed-ish fetchers for the database connector —
 * /api/v1/integration/database/* (ADR-0022).
 *
 * Like the Veeva and Blob browse wrappers, the backend returns plain dicts for
 * these reads (they mirror an external system's own shape), so the response
 * types here are hand-written interfaces rather than OpenAPI-generated ones.
 *
 * **Reads only.** There is no write wrapper here because there is no write
 * endpoint: the connector refuses anything that is not a single SELECT, and it
 * refuses it server-side — nothing in this file is a security control.
 */

import { apiFetch } from "./apiFetch";

export interface DatabaseEngine {
  engine: string;
  label: string;
  default_port: number;
  /** `database` for most engines; Oracle addresses a `service_name`. */
  addresses: "database" | "service_name";
  /** Whether this build can demand an encrypted transport for the engine. */
  encrypt_option: boolean;
}

export interface SchemaSummary {
  name: string;
  table_count: number;
}

export interface TableSummary {
  schema: string;
  name: string;
  kind: "table" | "view";
  /** A planner estimate, not a COUNT(*) — absent for views and for engines
   *  that will not answer cheaply. */
  row_estimate: number | null;
}

export interface ColumnInfo {
  name: string;
  type_name: string;
  nullable: boolean;
  primary_key: boolean;
}

export interface ForeignKey {
  columns: string[];
  referred_schema: string | null;
  referred_table: string | null;
  referred_columns: string[];
}

export interface TableSchema {
  schema: string;
  name: string;
  columns: ColumnInfo[];
  primary_key: string[];
  foreign_keys: ForeignKey[];
}

export interface DatabaseRows {
  rows: Record<string, unknown>[];
  /** From the result itself, so an empty table still reports its columns. */
  columns: string[];
  row_count: number;
  /** True when the read stopped at `max_rows` — shown, never swallowed. */
  truncated: boolean;
  max_rows: number;
  mode: "table" | "query";
  schema: string;
  table: string;
}

export interface ReadRowsIn {
  schema?: string;
  table?: string;
  /** A read-only SELECT. Wins over schema/table when both are given. */
  query?: string;
  limit?: number;
}

const base = (connectionId: string) =>
  `/api/v1/integration/database/${encodeURIComponent(connectionId)}`;

/** The engines this backend speaks — drives the connection form, so the form
 *  can never offer one the backend would reject. */
export function databaseEngines(token: string): Promise<{ items: DatabaseEngine[] }> {
  return apiFetch<{ items: DatabaseEngine[] }>("/api/v1/integration/database/engines", { token });
}

export function databaseSchemas(
  token: string,
  connectionId: string,
): Promise<{ items: SchemaSummary[] }> {
  return apiFetch<{ items: SchemaSummary[] }>(`${base(connectionId)}/schemas`, { token });
}

export function databaseTables(
  token: string,
  connectionId: string,
  schema: string,
): Promise<{ items: TableSummary[] }> {
  return apiFetch<{ items: TableSummary[] }>(
    `${base(connectionId)}/tables?schema=${encodeURIComponent(schema)}`,
    { token },
  );
}

export function databaseColumns(
  token: string,
  connectionId: string,
  schema: string,
  table: string,
): Promise<TableSchema> {
  return apiFetch<TableSchema>(
    `${base(connectionId)}/columns?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}`,
    { token },
  );
}

/** Read rows — a table, or a read-only SELECT. Audited server-side
 *  (`database.rows_read`): rows leaving a customer's database are an access
 *  event, the same as a blob download. */
export function databaseRead(
  token: string,
  connectionId: string,
  input: ReadRowsIn,
): Promise<DatabaseRows> {
  return apiFetch<DatabaseRows>(`${base(connectionId)}/read`, {
    method: "POST",
    token,
    json: input,
  });
}
